(ns story-builder.story-editor.interactive.util
  (:require
   [clojure.set :as set]
   [clojure.string :as str]
   [story-builder.interactive-list.db :as interactive-list.db]
   [story-builder.util.core :as util]))

(def brand-story-max-options 7)

(def results-layer-types #{"quiz-results" "poll-results"})
(def narrow-interactive-types #{"IMAGE_QUIZ" "BINARY_POLL" "IMAGE_POLL" "RESULTS" "QUIZ_RESULTS" "POLL_RESULTS"})
(def narrow-interactive-layer-types (into #{"image-quiz" "binary-poll" "image-poll"} results-layer-types))

(def interactive-layer-types
  (into #{"slider" "quiz" "poll"}
        narrow-interactive-layer-types))

(def type->layer-type
  {"QUIZ"         :quiz
   "IMAGE_QUIZ"   :image-quiz
   "POLL"         :poll
   "BINARY_POLL"  :binary-poll
   "IMAGE_POLL"   :image-poll
   "QUIZ_RESULTS" :quiz-results
   "POLL_RESULTS" :poll-results
   "SLIDER"       :slider})

(def results-layer-layer-types #{:result-prompt :result-category :result-description})

(defn layer->interactive [layer]
  {:type     ((set/map-invert type->layer-type) (keyword (:type layer)))
   :question (get-in layer [:amp :prompt :text])
   :config   {:amp (:amp layer)}
   :id       (:interactive-id layer)})

(defn merge-interactive-into-layer
  [layer interactive]
  (-> layer
      (assoc :interactive-id (:id interactive))
      (assoc-in [:amp :prompt :text] (:question interactive))
      (assoc-in [:amp :options] (get-in interactive [:config :amp :options]
                                        (-> (get (interactive-list.db/defaults) (type->layer-type (:type interactive)))
                                            :config :amp :options)))))

(defn auto-height? [story-type {:keys [type]}]
  (if (= story-type "branded")
    (contains? #{"slider" "quiz-results" "poll-results"} type)
    (contains? interactive-layer-types type)))

(defn global-variables [interactive-settings]
  (let [{:keys [accent-color
                correct-color
                incorrect-color
                background
                border-color
                border-radius
                border-style
                border-width
                padding]} interactive-settings]
    (->> (merge {"--NWSStoryInteractive-accentColor"  accent-color
                 "--NWSStoryInteractive-background"   background
                 "--NWSStoryInteractive-padding"      (->> padding
                                                           (map (fn [v] (str v "em")))
                                                           (str/join " "))
                 "--NWSStoryInteractive-borderColor"  border-color
                 "--NWSStoryInteractive-borderRadius" (->> border-radius
                                                           (map (fn [v] (str v "em")))
                                                           (str/join " "))
                 "--NWSStoryInteractive-borderStyle"  border-style
                 "--NWSStoryInteractive-borderWidth"  border-width}
                (when correct-color
                  {"--NWSStoryInteractive-correctColor" correct-color})
                (when incorrect-color
                  {"--NWSStoryInteractive-incorrectColor" incorrect-color}))
         (util/remove-by str/blank?))))

(defn get-font-id
  "Get font-id from either :font-id or [:font :id] format for backward compatibility"
  [data {:keys [style-path]
         :or   {style-path [:style]}}]
  (or (:font-id data)
      (get-in data [:font :id])
      (get-in data (into style-path [:font :id])
              "6c636fae-8e4b-11f0-96d0-b7fbbdadcee6")))

(defn prompt-variables [prompt {:keys [fonts]}]
  (->> {"--NWSStoryInteractive-prompt-background"   (get prompt :background)
        "--NWSStoryInteractive-prompt-borderColor"  (get prompt :border-color)
        "--NWSStoryInteractive-prompt-borderStyle"  (get prompt :border-style)
        "--NWSStoryInteractive-prompt-borderWidth"  (get prompt :border-width)
        "--NWSStoryInteractive-prompt-borderRadius" (when-let [border-radius (get prompt :border-radius)]
                                                      (->> border-radius
                                                           (map (fn [v] (str v "em")))
                                                           (str/join " ")))
        "--NWSStoryInteractive-prompt-color"        (get prompt :text-color)
        "--NWSStoryInteractive-prompt-fontSize"     (when-let [font-size (get prompt :font-size)]
                                                      (str font-size "em"))
        "--NWSStoryInteractive-prompt-fontStyle"    (get prompt :font-style)
        "--NWSStoryInteractive-prompt-fontWeight"   (get prompt :font-weight)
        "--NWSStoryInteractive-prompt-fontFamily"   (util/single-quoted (:name (get fonts (get-font-id prompt {}))))
        "--NWSStoryInteractive-prompt-textAlign"    (get prompt :alignment)
        "--NWSStoryInteractive-prompt-padding"      (->> (get prompt :padding)
                                                         (map (fn [v] (str v "em")))
                                                         (str/join " "))}
       (util/remove-by str/blank?)))

(defn option-variables
  [option {:keys [fonts
                  prefix
                  style-path]
           :or   {prefix     "--NWSStoryInteractive-options-"
                  style-path [:style]}}]
  (let [get-style-prop  (fn [prop] (get-in option (into style-path [prop])))
        get-direct-prop (fn [prop] (get option prop))]
    (->> {(str prefix "padding")      (some->> (get-style-prop :padding)
                                               (map (fn [v] (str v "em")))
                                               (str/join " "))
          (str prefix "margin")       (some->> (get-style-prop :margin)
                                               (map (fn [v] (str v "em")))
                                               (str/join " "))
          (str prefix "textAlign")    (get-direct-prop :alignment)
          (str prefix "fontSize")     (when-let [font-size (get-style-prop :font-size)]
                                        (str font-size "em"))
          (str prefix "fontStyle")    (get-style-prop :font-style)
          (str prefix "fontWeight")   (get-style-prop :font-weight)
          (str prefix "color")        (or (get-style-prop :text-color)
                                          (get-direct-prop :text-color))
          (str prefix "fontFamily")   (util/single-quoted (:name (get fonts (get-font-id option {:style-path style-path}))))
          (str prefix "background")   (get-style-prop :background)
          (str prefix "borderColor")  (or (get-style-prop :border-color)
                                          (get-direct-prop :border-color))
          (str prefix "borderRadius") (some->> (or (get-style-prop :border-radius)
                                                   (get-direct-prop :border-radius))
                                               (map (fn [v] (str v "em")))
                                               (str/join " "))
          (str prefix "borderStyle")  (get-style-prop :border-style)
          (str prefix "borderWidth")  (get-style-prop :border-width)
          ;; The below only really apply to the options block.
          (str prefix "spacing")      (when-let [spacing (get-direct-prop :spacing)]
                                        (str spacing "em"))}
         (util/remove-by str/blank?))))

(defn list-style-sequence [list-style-type]
  (case list-style-type
    "upper-alpha"          "ABCDEFG"
    "lower-alpha"          "abcdefg"
    "upper-roman"          ["I" "II" "III" "IV" "V" "VI" "VII"]
    "lower-roman"          ["i" "ii" "iii" "iv" "v" "vi" "vii"]
    "decimal"              "1234567"
    "decimal-leading-zero" ["01" "02" "03" "04" "05" "06" "07"]
    "upper-greek"          ["Α" "Β" "Γ" "Δ" "Ε" "Ζ" "Η"]
    "lower-greek"          ["α" "β" "γ" "δ" "ε" "ζ" "η"]
    ;; For Armenian and Georgian numbering, there isn't a straightforward linear pattern like the others.
    ;; This sequence might not be strictly correct, but is based on the initial letters.
    "armenian"             ["Ա" "Բ" "Գ" "Դ" "Ե" "Զ" "Է"]
    "georgian"             ["ა" "ბ" "გ" "დ" "ე" "ვ" "ზ"]
    ;; For Hebrew, the sequence is also based on initial letters.
    "hebrew"               (reverse ["א" "ב" "ג" "ד" "ה" "ו" "ז"])))
